import { NgClass } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faInbox } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { HttpError } from 'src/app/core/http-error';
import { Box } from 'src/app/models/box';
import { Rights } from 'src/app/models/enums/rights';
import { IBoxAccessibility } from 'src/app/models/interfaces/box-accessibility.interface';
import { IBox } from 'src/app/models/interfaces/box.interface';
import { BoxesService } from 'src/app/services/boxes.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { FormMode } from '../../controls/common/form-mode';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { BoxDetailContext } from './box-detail-context';
import { BoxDetailComponent } from './box-detail/box-detail.component';
import { BoxesListComponent } from './boxes-list/boxes-list.component';
import { BoxesTreeComponent } from './boxes-tree/boxes-tree.component';

enum DisplayMode {
    List = 1,
    Tree = 2,
    Form = 4,
}

@Component({
    selector: 'app-boxes',
    templateUrl: './boxes.component.html',
    styleUrls: ['./boxes.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, BoxesListComponent, BoxesTreeComponent, DetailHeaderComponent, BoxDetailComponent],
})
export class BoxesComponent implements OnInit {
    /**
     * Reference au composant BoxesList.
     */
    @ViewChild(BoxesListComponent) boxesList: BoxesListComponent;

    /**
     * Reference au composant BoxesTree.
     */
    @ViewChild(BoxesTreeComponent) boxesTree: BoxesTreeComponent;

    /**
     * Liste des icônes utilisés dans le template
     */
    public faInbox = faInbox;

    /**
     * Liste des enums utilisés dans le template
     */
    public FormMode = FormMode;
    public DisplayMode = DisplayMode;

    /**
     * Contexte du formulaire.
     */
    public context: BoxDetailContext = null;

    /**
     * Mode d'affichage de la liste (Liste, Arborescence, formulaire)
     */
    public displayMode = DisplayMode.List;

    /**
     * Indique si le bouton d'ajout/Edition doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     * Constructeur.
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param boxesService : Service métier utilisé pour gérer les bannettes.
     * @param memoryService : Service utilisé pour gérer le contexte courant.
     */
    constructor(
        protected notificationService: NotificationService,
        private boxesService: BoxesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {}

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.BoxCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.BoxEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne une bannette.
     * @param box Bannette selectionnée.
     */
    public onBoxSelected(box: Box) {
        this.createContext();
        this.loadBox(box.id).subscribe((x) => {
            this.refresh(Box.createFormCopy(x), FormMode.Reading);
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param box Bannette sélectionnée.
     */
    public onBoxDisplayed(box: Box) {
        this.createContext();
        this.loadBox(box.id).subscribe((x) => {
            this.refresh(Box.createFormCopy(x), FormMode.Reading);
        });
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param box Bannette sélectionnée.
     */
    public onBoxEdited(box: Box) {
        this.createContext();
        // Cas de la mise à jour.
        this.loadBox(box.id).subscribe((x) => {
            this.refresh(Box.createFormCopy(x), FormMode.Edition);
        });
    }

    /**
     * Appelé lorsque les données de la bannette ont été mise à jour.
     * Il s'agit des modifications apportées à l'onglet Infos.
     * @param box : Valeur de la bannette retourné par le formulaire.
     */
    public onBoxUpdated(box: Box): void {
        if (this.context.editedBoxId) {
            // Cas de la mise à jour.
            this.boxesService.update(this.context.editedBoxId, box).subscribe({
                next: (x) => {
                    this.refreshListOrTree(x.groupId);
                    if (box.groupId !== x.groupId) {
                        // Si le groupe a changé de parent --> Raffraichissement de l'ancien parent.
                        this.refreshListOrTree(box.groupId);
                    }
                    this.refreshWithNotification(Box.createFormCopy(x), 'bannette mise à jour', FormMode.Reading);
                },
                error: (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour la bannette.' + error.message);
                    this.spinner.hide('main-spinner');
                },
            });
        } else {
            // Cas de la création.
            this.boxesService.add(box).subscribe({
                next: (x) => {
                    this.refreshListOrTree(x.groupId);
                    this.refreshWithNotification(Box.createFormCopy(x), 'bannette créée', FormMode.Reading);
                },
                error: (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer la bannette.' + error.message);
                    this.spinner.hide('main-spinner');
                },
            });
        }
    }

    public onBoxesSorted(boxes: Box[]) {
        this.spinner.show('main-spinner');
        this.boxesService.applyOrderChanges(boxes).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, "L'ordre des bannettes a été appliqué");
                this.boxesList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible dappliquer l'ordre des bannettes." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appelé lorsque les accès aux bannettes ont été mis à jour.
     * Il s'agit des modifications apportées à l'onglet Accessibilité.
     * @param boxesAccessibility : Accès aux bannettes à mettre à jour.
     */
    public onAccessibilitiesUpdated(accessibilities: IBoxAccessibility[]): void {
        this.boxesService.updateAccessibilities(this.context.editedBoxId, accessibilities).subscribe((x) => {
            this.refreshWithNotification(this.context.boxContext.box, 'Accessibilité de la bannette mis à jour', FormMode.Reading);
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onBoxCreated() {
        this.displayForm(Box.createFormCopy(new Box()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param box Bannette sélectionnée.
     */
    public onBoxDuplicated(box: Box) {
        let duplicatedBox = Box.createFormCopy(box);
        duplicatedBox.id = null;
        this.displayForm(duplicatedBox, FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param box Bannette sélectionnée.
     */
    public onBoxDeleted(box: Box) {
        // Suppression effective de la bannette.
        this.boxesService.delete(box.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'bannette supprimée');
                this.refreshListOrTree(box.groupId);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'La suppression de la bannette a échoué.');
            }
        );
    }

    /**
     * Appllé lors de l'export.
     */
    public onBoxesExported() {
        this.createContext();
        this.boxesList.export('Bannettes.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onBoxesImported() {
        this.boxesList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onBoxesHelped() {
        this.boxesList.displayHelpDialog('boxes');
    }

    /**
     * Retourne une valeur indiquant si le formulaire est affiché (visible)
     * @returns True si le formulaire est visible.
     */
    public isFormDisplayed(): boolean {
        return DisplayMode.Form === (this.displayMode & DisplayMode.Form);
    }

    /**
     * Retourne une valeur indiquant si l'affichage des bannettes sous la forme d'une arborescence est actif.
     * L'affichage des bannettes peut être caché si dans le même temps le formulaire est visible.
     * @returns True si l'affichage des bannettes sous la forme d'une arborescence est actif.
     */
    public isTreeDisplayed(): boolean {
        return DisplayMode.Tree === (this.displayMode & DisplayMode.Tree);
    }

    /**
     * Retourne une valeur indiquant si l'affichage des bannettes sous la forme d'une liste est actif.
     * L'affichage des bannettes peut être caché si dans le même temps le formulaire est visible.
     * @returns True si l'affichage des bannettes sous la forme d'une liste est actif.
     */
    public isListDisplayed(): boolean {
        return DisplayMode.List === (this.displayMode & DisplayMode.List);
    }

    /**
     * Cache le formulaire.
     * La liste ou l'arboresence est alors visible.
     */
    public hideForm(): void {
        this.displayMode &= ~DisplayMode.Form;
    }

    /**
     * Définit le mode d'affichage de la page (Form / List / Tree).
     * Les modes List et Tree sont exclusifs. Par contre le mode Form peut être associés à l'un des 2 autres.
     * Exemple : si le mode est à Tree, l'appel de setDisplayMode(DisplayMode.Form) affichera le formulaire mais un click sur le header 'Groupes d'utilisateurs' affichera l'arborescence.
     * si le mode est à List, l'appel de setDisplayMode(DisplayMode.Form) affichera le formulaire mais un click sur le header 'Groupes d'utilisateurs' affichera la liste.
     * @param mode Mode d'affichage
     */
    public setDisplayMode(mode: DisplayMode) {
        if (mode == DisplayMode.Form) {
            this.displayMode |= mode;
        } else {
            this.displayMode = mode;
        }
    }

    /**
     * Charge les informations d'une bannette.
     * Il s'agit des données affichées dans l'onglet Infos du détail.
     * @param boxId : Id de la bannette à charger.
     * @returns Les informations de la bannette.
     */
    public loadBox(boxId: string): Observable<Box> {
        return this.boxesService.get(boxId);
    }

    /**
     * Affiche Les données d'une bannette dans le formulaire.
     * @param box : Instance de la bannette à afficher dans le formulaire.
     * @param formMode Mode d'affichage du formulaire.
     */
    displayForm(box: IBox, formMode: FormMode): void {
        let context = this.fillForm(box);
        context.boxContext.formVisible = true;
        context.formMode = formMode;

        this.context = context;

        this.setDisplayMode(DisplayMode.Form);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param user : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    public fillForm(box: IBox): BoxDetailContext {
        let context = this.createContext();
        context.boxContext.box = box;
        context.editedBoxId = box.id;
        return context;
    }

    /**
     * Raffraichit le contenu de la liste ou de l'arborescence.
     * @param boxGroupId Id du groupe de bannettes dont l'affichage doit être raffraichi.
     */
    public refreshListOrTree(boxGroupId: string) {
        if (this.isListDisplayed()) {
            this.boxesList.refresh();
        } else {
            this.boxesTree.refresh(boxGroupId);
        }
    }

    /**
     * Rafraichi la page.
     * @param box : Valeur du client retourné par le formulaire.
     * @param mode : Mode d'affichage.
     */
    public refresh(box: IBox, mode: FormMode): void {
        this.boxesList.refresh();
        this.displayForm(box, mode);
        this.fillForm(box);
    }

    /**
     * Rafraichi la page.
     * @param box : Valeur du client retourné par le formulaire.
     * @param notificationString: Texte de la notification.
     * @param mode : Mode d'affichage.
     */
    public refreshWithNotification(box: IBox, notificationString: string, mode: FormMode): void {
        this.refresh(box, mode);
        this.notificationService.displaySuccess(null, notificationString);
        this.setDisplayMode(DisplayMode.Form);
    }

    /**
     * Créer un nouveau contexte.
     */
    private createContext(): BoxDetailContext {
        let context = new BoxDetailContext();
        return context;
    }
}
